import { useCallback } from 'react'
import { Link } from 'react-router-dom'
import { useTheme } from 'styled-components'

import { OrderType, PositionsSortByType } from '@/shared/backend/types/leaderboard'

import { IconArrowUp } from 'components/Icons/IconArrowUp'
import Pagination from 'components/Pagination/Pagination'
import { HeaderContainer, RowWrapper, TableContainer, Td, Th } from '../components/Table'
import { ellipsizeTokenAddress, getDisplayTimestamp, getPositionStatusLabel } from 'utils/helpers'
import { ProtocolLpPool } from 'modules/MyPositions/components/ProtocolLpPool'
import { Typography } from 'components/Typography/Typography'
import { getFormattedFeeRate } from './utils'
import { Status } from 'modules/MyPositions/MyPositions'
import { PositionRange } from 'modules/MyPositions/components/PositionRange'
import { getFormattedUsdValue } from 'utils/usdFormatters'
import { getPctValue } from 'utils/pctFormatter'
import { ClonePositionLink } from '../components/ClonePositionLink'
import { IconArrowDown } from 'components/Icons/IconArrowDown'
import { LeaderboardPosition } from '../hooks/useLeaderboardPositions'
import { TableContentLoader } from '../components/TableContentLoader'
import { Tooltip } from 'components/Tooltip/Tooltip'

interface Props {
  sortBy: PositionsSortByType
  handleColumnClick: (columnKey: PositionsSortByType) => void
  sortOrder: OrderType
  paginatedItems: LeaderboardPosition[]
  currentPage: number
  totalPages: number
  handlePageChange: (page: number) => void
  isLoading: boolean
}

export const PositionsTable = ({
  sortBy,
  handleColumnClick,
  sortOrder,
  paginatedItems,
  currentPage,
  totalPages,
  handlePageChange,
  isLoading,
}: Props) => {
  const theme = useTheme()

  const getSortIndicator = useCallback(
    (key: PositionsSortByType) => {
      if (sortBy === key) {
        return sortOrder === 'asc' ? <IconArrowUp /> : <IconArrowDown />
      }
      return null
    },
    [sortBy, sortOrder]
  )

  return (
    <TableContainer>
      <HeaderContainer style={{ position: 'relative' }}>
        <Th variant="body" style={{ flexBasis: 30, cursor: 'auto' }} $disabled={isLoading}>
          #
        </Th>
        <Th variant="body" style={{ flexBasis: 140, cursor: 'auto' }} $disabled={isLoading}>
          Wallet
        </Th>
        <Th
          variant="body"
          fontWeight={sortBy === 'positionCreationTimestampMs' ? 'semiBold' : 'regular'}
          style={{ flexBasis: 130 }}
          $disabled={isLoading}
        >
          <div onClick={() => handleColumnClick('positionCreationTimestampMs')}>
            Date {getSortIndicator('positionCreationTimestampMs')}
          </div>
        </Th>
        <Th variant="body" style={{ flexBasis: 100, cursor: 'auto' }} $disabled={isLoading}>
          Pool
        </Th>
        <Th variant="body" style={{ flexBasis: 100, cursor: 'auto' }} $disabled={isLoading}>
          Status
        </Th>
        <Th variant="body" style={{ flexBasis: 200, cursor: 'auto' }} $disabled={isLoading}>
          Range
        </Th>
        <Tooltip
          position="top"
          content="User’s equity share in the position."
          tooltipBoxStyles={{ maxWidth: 260, minWidth: 260 }}
          style={{ display: 'flex', alignItems: 'center', flexBasis: 160 }}
        >
          <Th
            variant="body"
            fontWeight={sortBy === 'timeWeightedEquity' ? 'semiBold' : 'regular'}
            style={{ flexBasis: 160 }}
            $disabled={isLoading}
          >
            <div onClick={() => handleColumnClick('timeWeightedEquity')}>
              Equity (Lev) {getSortIndicator('timeWeightedEquity')}
            </div>
          </Th>
        </Tooltip>
        <Tooltip
          position="top"
          content="Leveraged APR after rewards and borrowing costs."
          tooltipBoxStyles={{ maxWidth: 380, minWidth: 380 }}
          style={{ display: 'flex', alignItems: 'center', flexBasis: 90 }}
        >
          <Th
            variant="body"
            fontWeight={sortBy === 'aprFull' ? 'semiBold' : 'regular'}
            style={{ flexBasis: 90 }}
            $disabled={isLoading}
          >
            <div onClick={() => handleColumnClick('aprFull')}>APR {getSortIndicator('aprFull')}</div>
          </Th>
        </Tooltip>
        <Tooltip
          position="top"
          content="Realized PnL after yield, price impact, and borrowing costs."
          style={{ display: 'flex', alignItems: 'center', flexBasis: 100 }}
        >
          <Th
            variant="body"
            fontWeight={sortBy === 'pnl' ? 'semiBold' : 'regular'}
            style={{ flexBasis: 100 }}
            $disabled={isLoading}
          >
            <div onClick={() => handleColumnClick('pnl')}>PnL {getSortIndicator('pnl')}</div>
          </Th>
        </Tooltip>
        <Tooltip
          position="top"
          content="PnL as % of initial equity."
          tooltipBoxStyles={{ maxWidth: 190, minWidth: 190 }}
          style={{ display: 'flex', alignItems: 'center', flexBasis: 100 }}
        >
          <Th
            variant="body"
            fontWeight={sortBy === 'returnOnEquity' ? 'semiBold' : 'regular'}
            style={{ flexBasis: 100 }}
            $disabled={isLoading}
          >
            <div onClick={() => handleColumnClick('returnOnEquity')}>ROI {getSortIndicator('returnOnEquity')}</div>
          </Th>
        </Tooltip>
        <Tooltip
          position="top"
          content="Benchmark: gain/loss vs. holding tokens."
          tooltipBoxStyles={{ maxWidth: 310, minWidth: 310 }}
          style={{ display: 'flex', alignItems: 'center', flexBasis: 100 }}
        >
          <Th
            variant="body"
            fontWeight={sortBy === 'hodlBenchmarkPnl' ? 'semiBold' : 'regular'}
            style={{ flexBasis: 100 }}
            $disabled={isLoading}
          >
            <div onClick={() => handleColumnClick('hodlBenchmarkPnl')}>HODL {getSortIndicator('hodlBenchmarkPnl')}</div>
          </Th>
        </Tooltip>
      </HeaderContainer>
      {isLoading ? (
        <TableContentLoader />
      ) : (
        <div>
          {paginatedItems.map(position => (
            <RowWrapper key={position.positionId} $hideHover={true}>
              <Td variant="body" style={{ flexBasis: 30 }}>
                {position.rank}
              </Td>
              <Td variant="body" style={{ flexBasis: 140, cursor: 'pointer' }}>
                <Link to={`https://suivision.xyz/account/${position.ownerId}`} target="_blank" rel="noreferrer">
                  {ellipsizeTokenAddress(position.ownerId)}
                </Link>
              </Td>
              <Td variant="body" style={{ flexBasis: 130 }}>
                {position.displayInfo.creationTimestampMs
                  ? getDisplayTimestamp(position.displayInfo.creationTimestampMs)
                  : ''}
              </Td>
              <Td variant="body" style={{ flexBasis: 100, flexDirection: 'column', justifyContent: 'center' }}>
                <div style={{ display: 'flex' }}>
                  <ProtocolLpPool displayInfo={position.displayInfo} />
                </div>
                <Typography variant="caption" style={{ marginTop: 4 }}>
                  Pool: {getFormattedFeeRate(position.displayInfo.feeRate)}
                </Typography>
              </Td>
              <Td variant="body" style={{ flexBasis: 100 }}>
                <Status $type={position.displayInfo.status}>
                  {getPositionStatusLabel(position.displayInfo.status)}
                </Status>
              </Td>
              <Td
                variant="body"
                style={{
                  flexBasis: 200,
                  alignItems: 'center',
                  display: 'flex',
                }}
              >
                <PositionRange displayInfo={position.displayInfo} />
              </Td>
              <Td variant="body" style={{ flexBasis: 160 }}>
                {getFormattedUsdValue(position.timeWeightedEquity)}&nbsp;
                <span style={{ color: theme.colors.text.secondary }}>{`(${
                  position.displayInfo.leverage && position.displayInfo.leverage.isFinite()
                    ? position.displayInfo.leverage.toFixed(2) + 'x'
                    : '-'
                })`}</span>
              </Td>
              <Td variant="body" style={{ flexBasis: 90 }}>
                {typeof position.aprFull === 'number' ? getPctValue(position.aprFull) : '-'}
              </Td>
              <Td variant="body" style={{ flexBasis: 100 }}>
                {getFormattedUsdValue(position.pnl)}
              </Td>
              <Td variant="body" style={{ flexBasis: 100 }}>
                {getPctValue(position.returnOnEquity)}
              </Td>
              <Td variant="body" style={{ flexBasis: 100 }}>
                {getFormattedUsdValue(position.hodlBenchmarkPnl)}
              </Td>
              <ClonePositionLink position={position} />
            </RowWrapper>
          ))}
        </div>
      )}
      <Pagination
        currentPage={currentPage}
        totalPages={totalPages}
        onPageChange={handlePageChange}
        type="leaderboard-positions"
      />
    </TableContainer>
  )
}
